import { IStyle } from 'Styles';
import MiscService from 'business/services/MiscService';
import { ButtonsListContainer } from 'components/Button';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMDatePicker from 'components/customComponents/Controls/RMDatePicker';
import { LabelRadioBox } from 'components/customComponents/Layers/InputGroups';
import { Input } from 'components/input';
import React, { useState } from 'react';

import { downloadFile, showAlertSnackBar } from 'utils/Global';
import moment from 'moment';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { RMModalProps } from 'components/modal/RMModal';

interface Props {
  hideModal: () => void;
  showModal: (params: Omit<RMModalProps, 'showModal' | 'setShowModal'>) => void;
}

const MAX_DOWNLOAD_DAYS_LIMIT = 90;

const getTodayDateAtMidnight = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set time to midnight
  return today;
};

const getPastDate = (days: number) => {
  const today = new Date();
  today.setDate(today.getDate() - days); // Subtract 1 day
  today.setHours(0, 0, 0, 0);
  return today;
};

export default function DownloadLogModal(props: Props) {
  const { hideModal, showModal } = props;
  const [startDate, setStartDate] = useState<Date>(getPastDate(1));
  const [endDate, setEndDate] = useState<Date>(getTodayDateAtMidnight());
  const [daysDifference, setDaysDifference] = useState<number>(1);
  const [isFilterDateRange, setIsFilterDateRange] = useState<boolean>(false);
  const maxDaysLimit = getPastDate(MAX_DOWNLOAD_DAYS_LIMIT);

  const actionButtons: RMCustomButtonProps[] = [
    {
      title: 'CANCEL',
      contentContainerClass: 'bg-[#F7A246] w-[9.5rem] !text-[#ffffff]',
      onClick: () => {
        hideModal();
      },
    },
    {
      title: 'DOWNLOAD',
      isDisabled: daysDifference < 0,
      contentContainerClass: 'btn-outline-yellow-wide',
      onClick: () => {
        fetchLogs();
      },
    },
  ];

  const fetchLogs = async () => {
    if (startDate > endDate) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `Start date should be less than End Date`,
      );
      return;
    } else if (startDate < maxDaysLimit && endDate < maxDaysLimit) {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        `Maximum download period is ${MAX_DOWNLOAD_DAYS_LIMIT} days`,
      );
      return;
    } else {
      let interimStartDate = startDate;
      if (startDate < maxDaysLimit && endDate > maxDaysLimit) {
        setStartDate(maxDaysLimit);
        setDaysDifference(MAX_DOWNLOAD_DAYS_LIMIT);
        interimStartDate = maxDaysLimit;
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          `Maximum download period is ${MAX_DOWNLOAD_DAYS_LIMIT} days`,
        );
        return;
      }
      try {
        const miscService = new MiscService();
        const response = await miscService.downloadLogs(interimStartDate, endDate);
        if (response.data.size === 0) {
          showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, `No results found`);
        } else {
          hideModal();
          downloadFile(response.data, 'Logs.zip');
        }
      } catch (err) {
        hideModal();
        showModal({
          heading: 'Error',
          body: err.message ?? 'An error occured while downloading logs.',
          showButtons: true,
          dialogClassName: 'max-w-[600px] w-[600px]',
          headerClassName: 'text-black',
          isOkOnly: true,
          okButtonText: 'OK',
          onOk: () => hideModal(),
        });
      }
    }
  };

  const differenceInDays = (startDate, endDate) => {
    const startDateObj = moment(startDate);
    const endDateObj = moment(endDate);

    const dayDifference: number = endDateObj.diff(startDateObj, 'days');

    setDaysDifference(dayDifference);
  };

  return (
    <div id="downloadlogmodal-container" className="!w-full mb-3">
      <div id="downloadlogmodal-innercontainer">
        <h6 id="downloadlogmodal-title" className="!w-full mb-3">
          Choose a date range to download:
        </h6>
        <div
          id="downloadlogmodal-radiobox-logssince-wrapper"
          className="flex items-center"
        >
          <LabelRadioBox
            labelText={'Logs since (days)'}
            radioValue={!isFilterDateRange}
            style={styles.radioButton}
            onChange={() => {
              setIsFilterDateRange((prev) => !prev);
            }}
            name={'Logs since (days)'}
            disabled={false}
          />
        </div>
        <div
          id="downloadlogmodal-daysinput-wrapper"
          className="flex items-center ml-[3.32rem] mt-2"
        >
          <Input
            id="downloadlogmodal-daysinput"
            type="number"
            className="w-[5vw]"
            value={daysDifference}
            onChange={(e) => {
              setDaysDifference(Number(e.target.value));
              setStartDate(getPastDate(Number(e.target.value)));
              setEndDate(getTodayDateAtMidnight());
            }}
            disabled={isFilterDateRange}
            containerStyle={{ marginRight: '1rem' }}
          />
        </div>
        <div
          id="downloadlogmodal-radiobox-daterange-wrapper"
          style={{ display: 'flex', alignItems: 'center' }}
          className="mt-2"
        >
          <LabelRadioBox
            labelText={'Date Range'}
            radioValue={isFilterDateRange}
            style={styles.radioButton}
            onChange={() => {
              setIsFilterDateRange((prev) => !prev);
            }}
            name={'Date Range'}
            disabled={false}
          />
        </div>
        <div id="downloadlogmodal-daterange-container" className="flex mt-2">
          <div id="downloadlogmodal-fromdate-wrapper" className="w-1/2 flex items-center">
            <span id="downloadlogmodal-fromdate-label" className="w-3/10">
              From
            </span>
            <div id="downloadlogmodal-fromdate-pickerwrapper" className="w-7/10 ml-4">
              <RMDatePicker
                date={startDate}
                onChange={(e) => {
                  if (e <= new Date()) {
                    differenceInDays(e, endDate);
                    setStartDate(new Date(e));
                  } else {
                    differenceInDays(new Date(), endDate);
                    setStartDate(new Date());
                  }
                }}
              />
            </div>
          </div>
          <div id="downloadlogmodal-todate-wrapper" className="w-1/2 flex items-center">
            <span id="downloadlogmodal-todate-label" className="w-3/10">
              To
            </span>
            <div id="downloadlogmodal-todate-pickerwrapper" className="w-7/10 ml-4">
              <RMDatePicker
                date={endDate}
                onChange={(e) => {
                  if (e <= new Date()) {
                    differenceInDays(startDate, e);
                    setEndDate(new Date(e));
                  } else {
                    differenceInDays(startDate, new Date());
                    setEndDate(new Date());
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <ButtonsListContainer
        containerStyleClass="my-3 justify-end"
        marginBetweenItems={4}
        buttonsList={actionButtons}
      />
    </div>
  );
}

const styles: IStyle = {
  checkBox: {
    marginTop: '2%',
    marginBottom: '2%',
    width: '50%',
    justifyContent: 'flex-start',
  },
  radioButton: {
    width: '50%',
    justifyContent: 'flex-start',
    fontFamily: 'Roboto',
    fontWeight: 400,
  },
};
